import React, {useEffect, useMemo, useRef, useState} from 'react';
import {Tag} from "antd";
import {useCollection} from "react-firebase-hooks/firestore";
import {
    collection, doc, getDoc, getFirestore, orderBy, query, serverTimestamp, setDoc, updateDoc, writeBatch,
} from "firebase/firestore";
import {getAuth} from "firebase/auth";
import {useToast} from "@chakra-ui/react";
import moment from 'moment';
import {Link} from "react-router-dom";
import Firebase from "../../Firebase/Firebase";

const db = getFirestore(Firebase);

/** The support chat account users message (users/shopped-support). */
export const SUPPORT_UID = 'shopped-support';
const SUPPORT_NAME = 'Shopped Support';
const SUPPORT_IMAGE = 'https://www.shoppedmw.com/icon-512.png';

type ThreadStatus = 'open' | 'waiting' | 'resolved';
const STATUS_LABEL: Record<ThreadStatus, string> = {open: 'Open', waiting: 'Waiting on user', resolved: 'Resolved'};
const STATUS_COLOR: Record<ThreadStatus, string> = {open: 'red', waiting: 'blue', resolved: 'default'};

const btn: React.CSSProperties = {
    fontSize: 12, border: '1px solid #c7d2fe', borderRadius: 6, padding: '4px 10px',
    cursor: 'pointer', background: '#eef2ff', color: '#29327b',
};

/**
 * Sends a chat message from Shopped Support to [uid], written exactly like the
 * apps write one (both copies + both summaries). The user's copy (isRead:
 * false) triggers the existing new-message push.
 */
export async function sendSupportMessage(uid: string, text: string, user: {name?: string; image?: string} = {}) {
    const msgId = (crypto as any).randomUUID ? crypto.randomUUID() : String(Date.now());
    const msg = {msgId, authorId: SUPPORT_UID, text, createdAt: serverTimestamp(), type: 'text'};
    const batch = writeBatch(db);
    batch.set(doc(collection(db, 'users', SUPPORT_UID, 'messages', uid, 'chats')), {...msg, isRead: true});
    batch.set(doc(db, 'users', SUPPORT_UID, 'messages', uid), {
        ...msg, isRead: true, friendImage: user.image || '', friendUsername: user.name || '', uid,
        chatId: '', productId: '', productImage: '',
    });
    batch.set(doc(collection(db, 'users', uid, 'messages', SUPPORT_UID, 'chats')), {...msg, isRead: false});
    batch.set(doc(db, 'users', uid, 'messages', SUPPORT_UID), {
        ...msg, isRead: false, friendImage: SUPPORT_IMAGE, friendUsername: SUPPORT_NAME, uid: SUPPORT_UID,
        chatId: '', productId: '', productImage: '',
    });
    await batch.commit();
}

const fmt = (v: any) => {
    if (!v) return '';
    const d = typeof v.toDate === 'function' ? v.toDate() : v;
    return moment(d).calendar(null, {sameDay: 'HH:mm', lastDay: '[Yesterday] HH:mm', lastWeek: 'ddd HH:mm', sameElse: 'DD MMM'});
};

function preview(m: any): string {
    const t = m?.type;
    if (t === 'image') return '📷 Photo';
    if (t === 'video') return '🎥 Video';
    if (t === 'audio') return '🎤 Voice message';
    if (t === 'product') return '🛍️ Product';
    return m?.text || '';
}

/** Buyer / Seller per uid, read once (users/{uid}.type). */
function useRoles(uids: string[]) {
    const [roles, setRoles] = useState<Record<string, {role: string; name: string; image: string}>>({});
    useEffect(() => {
        const missing = uids.filter((u) => !(u in roles));
        if (!missing.length) return;
        Promise.all(missing.map(async (u) => {
            const s = await getDoc(doc(db, 'users', u)).catch(() => null);
            const d: any = s?.data() ?? {};
            return [u, {
                role: String(d.type || '').toLowerCase() === 'seller' ? 'Seller' : 'Buyer',
                name: d.fullName || '',
                image: d.userImage || '',
            }] as const;
        })).then((entries) => setRoles((r) => ({...r, ...Object.fromEntries(entries)})));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [uids.join(',')]);
    return roles;
}

function Thread({uid, summary, role, meta}: {uid: string; summary: any; role?: {role: string; name: string; image: string}; meta: any}) {
    const toast = useToast();
    const [text, setText] = useState('');
    const [sending, setSending] = useState(false);
    const [note, setNote] = useState(meta?.note ?? '');
    useEffect(() => setNote(meta?.note ?? ''), [meta?.note, uid]);
    const bottom = useRef<HTMLDivElement>(null);
    const [snap] = useCollection(query(collection(db, 'users', SUPPORT_UID, 'messages', uid, 'chats'), orderBy('createdAt', 'asc')));
    const msgs = snap?.docs ?? [];
    useEffect(() => { bottom.current?.scrollIntoView({block: 'end'}); }, [msgs.length]);

    // Opening the conversation marks it read for the team.
    useEffect(() => {
        if (summary && summary.isRead === false) {
            updateDoc(doc(db, 'users', SUPPORT_UID, 'messages', uid), {isRead: true}).catch(() => {});
        }
    }, [uid, summary]);

    const adminEmail = getAuth(Firebase).currentUser?.email ?? null;
    const saveMeta = (fields: any) =>
        setDoc(doc(db, 'supportThreads', uid), {...fields, updatedAt: serverTimestamp(), updatedBy: adminEmail}, {merge: true});

    /** Writes the reply exactly like the apps write a chat message (both copies + both summaries);
     * the user's copy (isRead: false) triggers the existing new-message push. */
    const send = async () => {
        const body = text.trim();
        if (!body) return;
        setSending(true);
        try {
            await sendSupportMessage(uid, body, {
                name: role?.name || summary?.friendUsername || '',
                image: role?.image || summary?.friendImage || '',
            });
            await saveMeta({status: 'waiting', lastReplyBy: adminEmail, lastReplyAt: serverTimestamp()});
            setText('');
        } catch (e: any) {
            toast({position: 'top', title: e.message, status: 'error', duration: 3000});
        } finally {
            setSending(false);
        }
    };

    const status: ThreadStatus = meta?.status ?? 'open';
    return (
        <div style={{display: 'flex', flexDirection: 'column', height: '70vh', border: '1px solid #eee', borderRadius: 10, background: '#fff'}}>
            <div style={{padding: '10px 14px', borderBottom: '1px solid #f0f0f0', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap'}}>
                <div style={{flex: 1, minWidth: 180}}>
                    <div style={{fontWeight: 700}}>
                        {role?.name || summary?.friendUsername || uid}{' '}
                        {role && <Tag color={role.role === 'Seller' ? 'purple' : 'cyan'}>{role.role}</Tag>}
                    </div>
                    <div style={{fontSize: 11, color: '#999'}}>
                        <Link to={`/buyers/${uid}`}>Open account</Link>
                        {meta?.lastReplyBy ? ` · last reply by ${meta.lastReplyBy}` : ''}
                    </div>
                </div>
                <select value={status} onChange={(e) => saveMeta({status: e.target.value})}
                        style={{fontSize: 12, padding: '4px 6px', borderRadius: 6, border: '1px solid #ddd'}}>
                    {(Object.keys(STATUS_LABEL) as ThreadStatus[]).map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
                </select>
            </div>

            <div style={{flex: 1, overflowY: 'auto', padding: 14, background: '#fafbfc'}}>
                {msgs.map((d) => {
                    const m: any = d.data();
                    const mine = m.authorId === SUPPORT_UID;
                    return (
                        <div key={d.id} style={{display: 'flex', justifyContent: mine ? 'flex-end' : 'flex-start', marginBottom: 8}}>
                            <div style={{
                                maxWidth: '72%', padding: '8px 12px', borderRadius: 14, fontSize: 13.5, whiteSpace: 'pre-wrap',
                                background: mine ? '#2F6FDE' : '#fff', color: mine ? '#fff' : '#222',
                                border: mine ? 'none' : '1px solid #eee',
                            }}>
                                {m.type === 'image' && m.uri
                                    ? <a href={m.uri} target="_blank" rel="noreferrer"><img src={m.uri} alt="" style={{maxWidth: 220, borderRadius: 8}}/></a>
                                    : preview(m)}
                                {m.deleted && <i style={{opacity: .7}}> (deleted)</i>}
                                <div style={{fontSize: 10, opacity: .7, marginTop: 3, textAlign: 'right'}}>{fmt(m.createdAt)}</div>
                            </div>
                        </div>
                    );
                })}
                {!msgs.length && <div style={{color: '#999', textAlign: 'center', padding: 30}}>No messages yet.</div>}
                <div ref={bottom}/>
            </div>

            <div style={{padding: 10, borderTop: '1px solid #f0f0f0', display: 'flex', gap: 8}}>
                <textarea value={text} onChange={(e) => setText(e.target.value)} rows={2}
                          placeholder={`Reply as ${SUPPORT_NAME}…`}
                          onKeyDown={(e) => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) send(); }}
                          style={{flex: 1, padding: 8, borderRadius: 8, border: '1px solid #e5e5e5', fontSize: 13.5, resize: 'vertical'}}/>
                <button style={{...btn, background: '#29327b', color: '#fff', alignSelf: 'stretch'}} disabled={sending || !text.trim()} onClick={send}>
                    {sending ? 'Sending…' : 'Send'}
                </button>
            </div>
            <div style={{padding: '0 10px 10px', display: 'flex', gap: 8}}>
                <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Internal note (only admins see this)"
                       style={{flex: 1, padding: '5px 8px', borderRadius: 8, border: '1px solid #eee', fontSize: 12.5}}/>
                <button style={btn} onClick={() => saveMeta({note: note.trim()}).then(() =>
                    toast({position: 'top', title: 'Note saved', status: 'success', duration: 1200}))}>Save note</button>
            </div>
        </div>
    );
}

/** Support chats: users' conversations with Shopped Support. */
export default function SupportChats() {
    const [summariesSnap, loading, error] = useCollection(query(collection(db, 'users', SUPPORT_UID, 'messages'), orderBy('createdAt', 'desc')));
    const [metaSnap] = useCollection(collection(db, 'supportThreads'));
    const [roleFilter, setRoleFilter] = useState<'All' | 'Buyer' | 'Seller'>('All');
    const [statusFilter, setStatusFilter] = useState<ThreadStatus | 'all'>('all');
    const [selected, setSelected] = useState<string | null>(null);

    const summaries = useMemo(() => summariesSnap?.docs ?? [], [summariesSnap]);
    const meta = useMemo(() => Object.fromEntries((metaSnap?.docs ?? []).map((d) => [d.id, d.data()])), [metaSnap]);
    const roles = useRoles(summaries.map((d) => d.id));

    const rows = summaries
        .filter((d) => roleFilter === 'All' || roles[d.id]?.role === roleFilter)
        .filter((d) => statusFilter === 'all' || ((meta[d.id] as any)?.status ?? 'open') === statusFilter)
        // Unread first, then newest.
        .sort((a, b) => Number(a.data().isRead !== false) - Number(b.data().isRead !== false));

    const sel = summaries.find((d) => d.id === selected);

    return (
        <div style={{display: 'flex', gap: 14, flexWrap: 'wrap'}}>
            <div style={{flex: '1 1 320px', maxWidth: 420}}>
                <div style={{display: 'flex', gap: 6, marginBottom: 8, flexWrap: 'wrap'}}>
                    {(['All', 'Buyer', 'Seller'] as const).map((r) => (
                        <button key={r} onClick={() => setRoleFilter(r)}
                                style={{...btn, background: roleFilter === r ? '#29327b' : '#eef2ff', color: roleFilter === r ? '#fff' : '#29327b'}}>
                            {r === 'All' ? 'All' : `${r}s`}
                        </button>
                    ))}
                    <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as any)}
                            style={{fontSize: 12, padding: '4px 6px', borderRadius: 6, border: '1px solid #ddd'}}>
                        <option value="all">Any status</option>
                        {(Object.keys(STATUS_LABEL) as ThreadStatus[]).map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
                    </select>
                </div>
                {loading && <div style={{padding: 20, color: '#999'}}>Loading…</div>}
                {error && <div style={{padding: 20, color: '#b91c1c'}}>{error.message}</div>}
                {!loading && rows.length === 0 && <div style={{padding: 20, color: '#999'}}>No conversations.</div>}
                {rows.map((d) => {
                    const s: any = d.data();
                    const unread = s.isRead === false;
                    const st: ThreadStatus = (meta[d.id] as any)?.status ?? 'open';
                    const r = roles[d.id];
                    return (
                        <div key={d.id} onClick={() => setSelected(d.id)}
                             style={{
                                 padding: 10, borderRadius: 10, marginBottom: 6, cursor: 'pointer',
                                 border: selected === d.id ? '1px solid #29327b' : '1px solid #eee',
                                 background: unread ? '#eef2ff' : '#fff',
                             }}>
                            <div style={{display: 'flex', justifyContent: 'space-between', gap: 6}}>
                                <span style={{fontWeight: unread ? 700 : 600, fontSize: 13.5}}>
                                    {r?.name || s.friendUsername || d.id}
                                </span>
                                <span style={{fontSize: 11, color: '#999'}}>{fmt(s.createdAt)}</span>
                            </div>
                            <div style={{fontSize: 12, color: '#666', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
                                {s.authorId === SUPPORT_UID ? 'You: ' : ''}{preview(s)}
                            </div>
                            <div style={{marginTop: 4}}>
                                {r && <Tag color={r.role === 'Seller' ? 'purple' : 'cyan'}>{r.role}</Tag>}
                                <Tag color={STATUS_COLOR[st]}>{STATUS_LABEL[st]}</Tag>
                                {unread && <Tag color="red">New</Tag>}
                            </div>
                        </div>
                    );
                })}
            </div>
            <div style={{flex: '2 1 420px'}}>
                {sel
                    ? <Thread key={sel.id} uid={sel.id} summary={sel.data()} role={roles[sel.id]} meta={meta[sel.id]}/>
                    : <div style={{padding: 40, color: '#999', textAlign: 'center', border: '1px dashed #eee', borderRadius: 10}}>
                        Select a conversation
                    </div>}
            </div>
        </div>
    );
}
